/**
 * FAMILY SUPPORT REGISTRY — Aggregator (Post-Refactor)
 *
 * This file aggregates support content from all module files into a single
 * FAMILY_SUPPORT_REGISTRY object for backwards compatibility.
 *
 * REFACTORED: 2026-03-25 (Step 5-6)
 * Previous: 4971 lines monolithic registry
 * Current: ~230 lines aggregator pattern
 *
 * Module files location: src/support/modules/
 * - get.support.ts (50 families - 100%)
 * - taa.support.ts (35 families - 100%)
 * - met.support.ts (40 entries: 39 taxonomy + 1 orphan MET-TIJD-DUUR)
 * - sww.support.ts (24 families - 100%)
 * - topo.support.ts (23 families - 100%)
 * - stu.support.ts (20 families - 100%)
 * - spr.support.ts (20 families - 100%)
 * - rek.support.ts (17 families - 100% ✅ COMPLETE)
 * - tijd.support.ts (16 families - 100%)
 * - geld.support.ts (15 families - 100%)
 *
 * Total: 260 entries (259 taxonomy + 1 orphan)
 * Taxonomy: 259 families
 * Coverage: 259/259 = 100% ✅
 * Orphan: 1 (MET-TIJD-DUUR - documented, kept for backwards compatibility)
 */

import { HintStep } from './hints';
import { type VisualAid } from '../visuals';
import { debug, warn, error as logError } from '../utils/logger';

// Import all module support registries
import { STU_SUPPORT, TOPO_SUPPORT, REK_SUPPORT, SPR_SUPPORT, SWW_SUPPORT, TAA_SUPPORT, MET_SUPPORT, TIJD_SUPPORT, GELD_SUPPORT, GET_SUPPORT } from './modules';

/**
 * Type Definitions
 * (Preserved from original registry)
 */
export interface RepresentationSupport {
  hints?: HintStep[];
  visual?: VisualAid;
  explanation?: string;
}
export interface FamilySupportEntry {
  familyId: string;
  hints?: HintStep[];
  defaultVisual?: VisualAid;
  defaultExplanation?: string;
  representations?: Record<string, RepresentationSupport>;
  /** Whether this family conceptually requires a visual aid */
  requiresVisual?: boolean;
  /** Whether this family conceptually requires hints */
  requiresHints?: boolean;
}

/**
 * FAMILY SUPPORT REGISTRY — Aggregated from Module Files
 *
 * Merges all module support registries into a single object.
 * This maintains backwards compatibility with existing code that imports
 * FAMILY_SUPPORT_REGISTRY directly.
 *
 * Module breakdown (sorted by size):
 * - GET: 50 families (Getalbegrip - largest, 100%)
 * - TAA: 35 families (Taalbegrip - 100%)
 * - MET: 40 entries (39 taxonomy + 1 orphan: MET-TIJD-DUUR)
 * - SWW: 24 families (Spelling Werkwoorden - 100%)
 * - TOPO: 23 families (Topografie - 100%)
 * - STU: 20 families (Studievaardigheden - 100%)
 * - SPR: 20 families (Spelling - 100%)
 * - REK: 17 families (Basisrekenen - 100% ✅)
 * - TIJD: 16 families (Tijd - 100%)
 * - GELD: 15 families (Geld - 100%)
 *
 * Total: 260 entries
 * Taxonomy families: 259
 * Coverage: 259/259 (100% ✅)
 * Orphan: 1 (MET-TIJD-DUUR - not in taxonomy, kept for backwards compatibility)
 */
export const FAMILY_SUPPORT_REGISTRY: Record<string, FamilySupportEntry> = {
  ...STU_SUPPORT,
  ...TOPO_SUPPORT,
  ...REK_SUPPORT,
  ...SPR_SUPPORT,
  ...SWW_SUPPORT,
  ...TAA_SUPPORT,
  ...MET_SUPPORT,
  ...TIJD_SUPPORT,
  ...GELD_SUPPORT,
  ...GET_SUPPORT
};

/**
 * Validation Types & Functions
 * (Preserved from original registry)
 */
export interface HintCoverageResult {
  valid: boolean;
  totalFamilies: number;
  familiesWithHints: number;
  coveragePercent: number;
  missingHints: string[];
  unknownFamilyIds: string[];
  emptyHints: string[];
}

/**
 * Validate hint coverage across all families
 *
 * Checks:
 * - All taxonomy families have support entries
 * - All support entries have hints
 * - No unknown familyIds in registry
 *
 * @returns HintCoverageResult with validation details
 */
export function validateHintCoverage(): HintCoverageResult {
  // Lazy import to avoid circular dependency
  const {
    TAXONOMY
  } = require('../content/taxonomy');
  const taxonomyFamilyIds = new Set<string>((TAXONOMY as {
    familyId: string;
  }[]).map(f => f.familyId));
  const registryFamilyIds = new Set<string>(Object.keys(FAMILY_SUPPORT_REGISTRY));
  const missingHints: string[] = [];
  const unknownFamilyIds: string[] = [];
  const emptyHints: string[] = [];
  for (const familyId of taxonomyFamilyIds) {
    const entry = FAMILY_SUPPORT_REGISTRY[familyId];
    if (!entry) {
      missingHints.push(familyId);
    } else if (!entry.hints || entry.hints.length === 0) {
      emptyHints.push(familyId);
    }
  }
  for (const familyId of registryFamilyIds) {
    if (!taxonomyFamilyIds.has(familyId)) {
      unknownFamilyIds.push(familyId);
    }
  }
  const familiesWithHints = [...taxonomyFamilyIds].filter(id => {
    const entry = FAMILY_SUPPORT_REGISTRY[id];
    return entry && entry.hints && entry.hints.length > 0;
  }).length;
  const totalFamilies = taxonomyFamilyIds.size;
  return {
    valid: missingHints.length === 0 && unknownFamilyIds.length === 0,
    totalFamilies,
    familiesWithHints,
    coveragePercent: totalFamilies > 0 ? Math.round(familiesWithHints / totalFamilies * 100) : 0,
    missingHints,
    unknownFamilyIds,
    emptyHints
  };
}

/**
 * Assert hint coverage (throws if validation fails)
 *
 * Used in development to ensure all families have proper support content.
 * Logs detailed error messages for missing or invalid entries.
 */
export function assertHintCoverage(): void {
  const result = validateHintCoverage();
  if (!result.valid) {
    logError('🚨 HINT COVERAGE VALIDATION FAILED:');
    if (result.missingHints.length > 0) {
      logError(`  ❌ ${result.missingHints.length} families missing hints: ${result.missingHints.slice(0, 10).join(', ')}${result.missingHints.length > 10 ? '...' : ''}`);
    }
    if (result.unknownFamilyIds.length > 0) {
      logError(`  ❌ ${result.unknownFamilyIds.length} unknown familyIds in registry: ${result.unknownFamilyIds.join(', ')}`);
    }
  } else {
    debug(`✅ Hint Coverage: ${result.familiesWithHints}/${result.totalFamilies} families (${result.coveragePercent}%)`);
    if (result.emptyHints.length > 0) {
      warn(`  ⚠️ ${result.emptyHints.length} families have empty hints: ${result.emptyHints.join(', ')}`);
    }
  }
}

/**
 * Helper Functions (Preserved for backwards compatibility)
 *
 * These functions were part of the original registry and are used by
 * supportResolver.ts and useHintEscalation.ts
 */

/**
 * Get support entry for a family
 * @param familyId - The family ID to look up
 * @returns FamilySupportEntry or undefined if not found
 */
export function getFamilySupportEntry(familyId: string): FamilySupportEntry | undefined {
  return FAMILY_SUPPORT_REGISTRY[familyId];
}

/**
 * Get hints for a family
 * @param familyId - The family ID to look up
 * @returns Array of hint steps, or empty array if not found
 */
export function getHintsForFamilyId(familyId: string): HintStep[] {
  const entry = FAMILY_SUPPORT_REGISTRY[familyId];
  return entry?.hints || [];
}

/**
 * Get all supported family IDs
 * @returns Array of all family IDs in the registry
 */
export function getAllSupportedFamilyIds(): string[] {
  return Object.keys(FAMILY_SUPPORT_REGISTRY);
}